import { captureWarning } from '@/wabunka-src/common/utils/sentry';
import { sanitizeSensitiveQueryInText } from '@/wabunka-src/common/utils/url/sanitizeSensitiveQuery';

type ZonesRegistry = Record<string, unknown>;
type RegistryWindow = Window & { google_tag_manager?: ZonesRegistry };

const ZONES_KEY = 'zones';

/** gtm.js は zones を 3 箇所から読み、それぞれ別のメソッドを呼ぶ。1 つでも欠けるとどれかで throw する */
const REQUIRED_ZONES_METHODS = ['isActive', 'getIsAllowedFn', 'unregisterChild'] as const;

const REPORTED_KEYS_LIMIT = 20;

const REJECTED_MESSAGE = 'GTM zones rejected';

let acceptedZones: unknown;
let reported = false;

/**
 * zones として採用してよい値か。
 *
 * 本物のレジストリはメソッドをプロトタイプに持つため、own プロパティで判定すると本物を拒否する。
 */
export const isUsableZones = (value: unknown): boolean => {
  if (value === null || (typeof value !== 'object' && typeof value !== 'function')) return false;
  try {
    return REQUIRED_ZONES_METHODS.every((name) => typeof (value as ZonesRegistry)[name] === 'function');
  } catch {
    // getter が throw する値は採用しない。setter から呼ぶため、ここで投げ返すと代入元の処理を壊す
    return false;
  }
};

const describeRejectedValue = (value: unknown): Record<string, unknown> => {
  try {
    return {
      type: value === null ? 'null' : typeof value,
      ownKeys: typeof value === 'object' || typeof value === 'function' ? Object.keys(value as object).slice(0, REPORTED_KEYS_LIMIT) : [],
    };
  } catch {
    return { type: 'unreadable' };
  }
};

/**
 * 代入を拒否したことを 1 ページロードにつき 1 回だけ報告する。
 *
 * 設置時点で既に入っていた値の拒否は報告しない。plugin は名前順に走り gtm.client.ts は sentry.ts の
 * init() より前に終わるため、そこでの capture は Sentry 内部で捨てられる。
 */
const reportRejection = (value: unknown) => {
  if (reported) return;
  reported = true;
  const stack = new Error().stack;
  try {
    // 報告は `extra` に載り、イベント URL を落とす event processor を通らない
    captureWarning(REJECTED_MESSAGE, 'wabunka', {
      stack: stack === undefined ? undefined : sanitizeSensitiveQueryInText(stack),
      value: describeRejectedValue(value),
    });
  } catch {
    // 報告できないこと自体は自衛の成否と関係ない。setter から呼ぶため、投げ返すと代入元の処理を壊す
  }
};

const readZones = () => acceptedZones;

const writeZones = (value: unknown) => {
  if (isUsableZones(value)) {
    acceptedZones = value;
    return;
  }
  // null / undefined は読み口を壊さない。拒否として扱わず、報告の枠も使わせない
  if (value === null || value === undefined) return;
  reportRejection(value);
};

const applyGuard = (registry: ZonesRegistry) => {
  let current: unknown;
  try {
    current = registry[ZONES_KEY];
  } catch {
    current = undefined;
  }
  if (isUsableZones(current)) acceptedZones = current;

  try {
    Object.defineProperty(registry, ZONES_KEY, { get: readZones, set: writeZones, configurable: true, enumerable: true });
  } catch {
    // アクセサを置けなくても、値さえ落とせれば読み口は「zones なし」に倒れる。
    // 再定義不可なら delete は false を返すだけなので、書き換え可なら代入で落とす
    if (!Reflect.deleteProperty(registry, ZONES_KEY)) {
      try {
        registry[ZONES_KEY] = undefined;
      } catch {
        // 再定義も書き換えも不可なら落とす手段が無い。ガード未設置と同じ状態に倒れる
      }
    }
  }
};

/**
 * `window.google_tag_manager.zones` に採用契約を課す。
 *
 * zones が無い状態はこのサイトの正常系で、gtm.js の読み口 3 箇所はすべて通常動作に倒れる。
 * レジストリを先に作っておくと、gtm.js の `w.google_tag_manager || {}` がこの器をそのまま引き継ぐ。
 */
export const installWabunkaGtmZonesGuard = () => {
  if (typeof window === 'undefined') return;
  try {
    const w = window as RegistryWindow;
    // 既存があれば代入しない。非 writable で置かれていると同じ値でも strict mode では TypeError になる
    applyGuard(w.google_tag_manager ?? (w.google_tag_manager = {}));
  } catch {
    // レジストリが非 writable や非オブジェクトで置かれていると器の用意も後始末も throw する。
    // plugin まで抜けると initWabunkaDataLayer() と page_view の登録が走らず、守るはずの送信を落とす
  }
};
